import React from 'react'
import { cn } from '../../utils/cn'

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * Visual style variant (4 variants only for consistency)
   */
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger'

  /**
   * Size variant
   */
  size?: 'sm' | 'md' | 'lg' | 'xl'

  /**
   * Loading state
   */
  loading?: boolean

  /**
   * Full width button
   */
  fullWidth?: boolean

  /**
   * Icon to display before text
   */
  iconBefore?: React.ReactNode

  /**
   * Icon to display after text
   */
  iconAfter?: React.ReactNode
}

const buttonVariants = {
  variant: {
    primary: 'bg-toast-gold text-white hover:bg-toast-gold/90 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed',
    secondary: 'bg-white text-gray-900 border-2 border-gray-200 hover:border-gray-300 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed',
    ghost: 'bg-transparent text-gray-700 hover:bg-gray-100 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed',
    danger: 'bg-red-600 text-white hover:bg-red-700 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed',
  },
  size: {
    sm: 'px-3 py-1.5 text-sm rounded-lg min-h-[36px]',
    md: 'px-4 py-2 text-base rounded-lg min-h-[44px]',
    lg: 'px-6 py-3 text-base rounded-lg min-h-[48px]',
    xl: 'px-8 py-4 text-lg rounded-lg min-h-[56px]',
  },
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = 'primary',
      size = 'md',
      loading = false,
      fullWidth = false,
      iconBefore,
      iconAfter,
      disabled,
      children,
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled || loading

    return (
      <button
        ref={ref}
        className={cn(
          // Base styles
          'inline-flex items-center justify-center gap-2',
          'font-medium transition-all duration-200',
          'focus:outline-none focus:ring-2 focus:ring-toast-gold focus:ring-offset-2',

          // Variant styles
          buttonVariants.variant[variant],

          // Size styles
          buttonVariants.size[size],

          // Full width
          fullWidth && 'w-full',

          // Custom className
          className
        )}
        disabled={isDisabled}
        {...props}
      >
        {loading && (
          <svg
            className="animate-spin h-4 w-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        )}

        {!loading && iconBefore && iconBefore}

        {children}

        {!loading && iconAfter && iconAfter}
      </button>
    )
  }
)

Button.displayName = 'Button'

export default Button
